import { useState, useEffect } from "react";
import api from "../../../../utils/api";
import {
  normalizeProducts,
  filterProducts,
  sortProducts,
  paginateData,
} from "../utils/productUtils";
import { ROWS_PER_PAGE, SORT_ORDER } from "../utils/constants";

/**
 * Custom hook for product list logic
 * Manages state and side effects for product listing
 *
 * @param {string} searchQuery - Search term
 * @returns {Object} Hook state and handlers
 */
export const useProductList = (searchQuery) => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    order: SORT_ORDER.ASC,
  });

  // Fetch products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await api.get("/products/combined");
        const normalized = normalizeProducts(response.data);
        setProducts(normalized);
        setFilteredProducts(normalized);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Filter and sort products when search or sort changes
  useEffect(() => {
    setPage(1); // Reset to page 1 when filters change

    let filtered = filterProducts(products, searchQuery);
    filtered = sortProducts(filtered, sortConfig.field, sortConfig.order);

    setFilteredProducts(filtered);
  }, [searchQuery, products, sortConfig]);

  /**
   * Handle column sort
   * @param {string} field - Field to sort by
   */
  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      order: prev.field === field ? prev.order * -1 : SORT_ORDER.ASC,
    }));
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredProducts.length / ROWS_PER_PAGE);
  const paginatedProducts = paginateData(filteredProducts, page, ROWS_PER_PAGE);
  const startIndex = (page - 1) * ROWS_PER_PAGE;

  return {
    products: paginatedProducts,
    loading,
    page,
    totalPages,
    startIndex,
    setPage,
    handleSort,
  };
};
